import {
  ImportCandidate,
  parsePlainText,
  parseSubtitles,
  parseTabularData,
  reconcileLemmas,
  stripArticle,
} from '@lingoping/core';
import { StorageManager } from '../storage/index.js';
import type { NewDeckWord } from './cloudDecks.js';
import { Normalizer, normalizeWords } from './normalizeService.js';
import { WordMeanings, meaningsOfWords } from './sensesService.js';

/** File types the Import panel accepts, for the file picker's `accept` attribute. */
export const SUPPORTED_IMPORT_EXTENSIONS = ['.txt', '.csv', '.tsv', '.srt', '.vtt', '.xlsx'];

/** An import candidate plus every surface form it was written in. */
export interface TriageCandidate extends ImportCandidate {
  knownForms: string[];
}

export interface ImportSummary {
  /** Raw terms found in the file/paste before deduplication. */
  totalFound: number;
  /** Repeat occurrences of the same term (case-insensitive) collapsed into one. */
  duplicates: number;
  /** Words the learner already knows or is already learning. They stay in the saved deck (its
   * status shows it), they are just not new. */
  alreadyKnown: number;
  /** Names, typos and non-German words dropped by normalization. */
  notWords: number;
  /** Whether words were reduced to dictionary forms by the AI (false: offline fallback). */
  normalized: boolean;
  /** Every unique word, in the order of the file: what is saved as a deck. */
  candidates: TriageCandidate[];
  /** How many of those are new to the learner (not known and not a card). */
  newWords: number;
}

function extensionOf(filename: string): string {
  const idx = filename.lastIndexOf('.');
  return idx >= 0 ? filename.slice(idx).toLowerCase() : '';
}

/** Tab-separated word lists saved as .txt end up here. */
function looksLikeTabularText(content: string): boolean {
  const lines = content
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0 && !l.startsWith('#'))
    .slice(0, 8);
  if (lines.length === 0) return false;
  const tabbed = lines.filter((l) => l.includes('\t')).length;
  return tabbed / lines.length >= 0.6;
}

function looksLikeSubtitleText(content: string): boolean {
  return /-->/.test(content) || /^WEBVTT/m.test(content);
}

/** Sentences → lemmas via the existing reconciliation engine, with no known-word filtering yet
 * (that happens once, uniformly, in `summarizeCandidates`). */
function candidatesFromSentences(sentences: string[], language: string): TriageCandidate[] {
  const { unknownLemmas } = reconcileLemmas(sentences, { language, knownWords: [] });
  return unknownLemmas.map((l) => ({
    term: l.lemma,
    context: l.contextSentence,
    knownForms: Array.from(new Set([l.lemma, ...l.rawForms])),
  }));
}

function candidatesFromTabular(
  entries: { term: string; translation?: string; contextSentence?: string }[]
): TriageCandidate[] {
  return entries
    .map((e) => {
      // Word lists often write nouns with their article ("das Fahrrad").
      const written = e.term.trim();
      const term = stripArticle(written);
      return {
        term,
        translation: e.translation?.trim() || undefined,
        context: e.contextSentence?.trim() || undefined,
        knownForms: Array.from(new Set([term, written])),
      };
    })
    .filter((c) => c.term.length > 0);
}

/** Parses one file into raw (not yet deduped or known-filtered) candidates, by extension. */
export async function extractCandidatesFromFile(
  file: File,
  language = 'de'
): Promise<TriageCandidate[]> {
  const ext = extensionOf(file.name);

  if (ext === '.xlsx') {
    const { parseExcelRows } = await import('../parsers/excelParser.js');
    const rows = await parseExcelRows(file);
    return candidatesFromTabular(rows.map((r) => ({ term: r.term, translation: r.translation })));
  }

  const content = await file.text();

  if (ext === '.srt' || ext === '.vtt') {
    return candidatesFromSentences(parseSubtitles(content).sentences, language);
  }
  if (ext === '.csv' || ext === '.tsv') {
    return candidatesFromTabular(parseTabularData(content));
  }
  if (ext === '.txt') {
    if (looksLikeTabularText(content)) return candidatesFromTabular(parseTabularData(content));
    if (looksLikeSubtitleText(content))
      return candidatesFromSentences(parseSubtitles(content).sentences, language);
    return candidatesFromSentences(parsePlainText(content).sentences, language);
  }

  throw new Error(
    `"${file.name}" isn't a supported file type. Use .txt, .csv, .tsv, .srt, .vtt or .xlsx.`
  );
}

/** Parses pasted text (subtitle cues, a tab/comma word list, or plain prose). */
export function extractCandidatesFromPaste(text: string, language = 'de'): TriageCandidate[] {
  if (looksLikeSubtitleText(text))
    return candidatesFromSentences(parseSubtitles(text).sentences, language);
  if (looksLikeTabularText(text)) return candidatesFromTabular(parseTabularData(text));
  return candidatesFromSentences(parsePlainText(text).sentences, language);
}

/**
 * Reduces every candidate to its dictionary form (when a normalizer is given and available) and
 * dedupes, in one uniform pass, keeping the order of first appearance. Words already known or
 * already a card are counted but kept: the import is saved as a deck, whose word status shows them.
 */
export async function summarizeCandidates(
  raw: TriageCandidate[],
  normalize?: Normalizer,
  language = 'de'
): Promise<ImportSummary> {
  const totalFound = raw.length;
  const merged = new Map<string, TriageCandidate>();
  let duplicates = 0;
  let notWords = 0;

  const normalized = normalize
    ? await normalize(
        raw.map((c) => c.term),
        language
      ).catch(() => null)
    : null;

  for (const original of raw) {
    let c = original;
    const n = normalized?.get(original.term.trim().toLowerCase());
    if (n) {
      if (!n.is_german_word || n.is_proper_noun) {
        notWords++;
        continue;
      }
      c = {
        ...original,
        term: n.lemma,
        knownForms: Array.from(new Set([n.lemma, original.term.trim(), ...original.knownForms])),
      };
    }
    const term = c.term.trim();
    if (!term) continue;
    const key = term.toLowerCase();
    const existing = merged.get(key);
    if (existing) {
      duplicates++;
      if (!existing.translation && c.translation) existing.translation = c.translation;
      if (!existing.context && c.context) existing.context = c.context;
      existing.knownForms = Array.from(new Set([...existing.knownForms, ...c.knownForms]));
      continue;
    }
    merged.set(key, { ...c, term });
  }

  // Known and learning words stay in the list (a deck is the whole file); they are only counted.
  const filterSet = await StorageManager.getKnownFilterSet();
  let alreadyKnown = 0;
  const candidates: TriageCandidate[] = [];
  for (const c of merged.values()) {
    const known =
      filterSet.has(c.term.toLowerCase()) ||
      c.knownForms.some((f) => filterSet.has(f.toLowerCase()));
    if (known) alreadyKnown++;
    candidates.push(c);
  }

  return {
    totalFound,
    duplicates,
    alreadyKnown,
    notWords,
    normalized: !!normalized,
    candidates,
    newWords: candidates.length - alreadyKnown,
  };
}

/**
 * The imported words as deck words: the file's order, with each word's sentence and translation.
 * A word with several separate meanings (`meanings`) becomes one entry per meaning, the meaning
 * its sentence uses first, so the learner decides each one ("Bank (Möbel)", "Bank (Geld)").
 */
export function candidatesToDeckWords(
  candidates: TriageCandidate[],
  meanings: ReadonlyMap<string, WordMeanings> = new Map()
): NewDeckWord[] {
  return candidates.flatMap((c): NewDeckWord[] => {
    const base = { lemma: c.term, context: c.context ?? null, hint: c.translation ?? null };
    const found = meanings.get(c.term.trim().toLowerCase());
    if (!found) return [base];
    const ordered = [
      ...found.senses.filter((s) => s.sense_key === found.suggested),
      ...found.senses.filter((s) => s.sense_key !== found.suggested),
    ];
    return ordered.map((sense) => ({
      ...base,
      // The file's translation is a hint for the meaning it was written for, not for the others.
      hint: sense.sense_key === (found.suggested ?? '') ? base.hint : null,
      sense_key: sense.sense_key,
      sense_label: sense.label,
      sense_hint: sense.hint || null,
    }));
  });
}

/** The same, after asking the server which of the words have several meanings. */
export async function candidatesToDeckWordsWithMeanings(
  candidates: TriageCandidate[],
  language = 'de'
): Promise<NewDeckWord[]> {
  const meanings = await meaningsOfWords(
    candidates.map((c) => ({ lemma: c.term, context: c.context })),
    language
  );
  return candidatesToDeckWords(candidates, meanings);
}

export async function importFromFile(file: File, language = 'de'): Promise<ImportSummary> {
  const raw = await extractCandidatesFromFile(file, language);
  return summarizeCandidates(raw, normalizeWords, language);
}

export async function importFromPaste(text: string, language = 'de'): Promise<ImportSummary> {
  const raw = extractCandidatesFromPaste(text, language);
  return summarizeCandidates(raw, normalizeWords, language);
}
